import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    TextField,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Chip,
    Divider,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    Autocomplete,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Avatar,
    LinearProgress,
    TableSortLabel,
    Backdrop,
    Tooltip,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Add as AddIcon,
    Search as SearchIcon,
    Person as PersonIcon,
    Save as SaveIcon,
    Label as LabelIcon,
    Remove as RemoveIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    FilterList as FilterListIcon,
    FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import * as contactService from '../../../services/contactService';

// Constants for optimization
const DEBOUNCE_DELAY = 300;
const CHUNK_SIZE = 5000;
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 500];

// Debounce utility
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

// Custom hook for high-performance filtering and sorting
const useOptimizedFilter = (data, search, tags, tagFilterMode, sortField, sortDirection) => {
    const searchIndex = useRef(new Map());
    const tagIndex = useRef(new Map());

    // Create indexes for fast search
    useEffect(() => {
        if (data.length === 0) return;

        const newSearchIndex = new Map();
        const newTagIndex = new Map();

        data.forEach(contact => {
            // Search index
            const searchText = [
                contact.name || '',
                contact.phone || '',
                contact.email || ''
            ].join(' ').toLowerCase();

            newSearchIndex.set(contact.id, searchText);

            // Tags index
            let contactTags = [];
            if (typeof contact.tags === 'string') {
                contactTags = contact.tags.split(',').map(tag => tag.trim().toLowerCase()).filter(Boolean);
            } else if (Array.isArray(contact.tags)) {
                contactTags = contact.tags.map(tag => {
                    if (typeof tag === 'string') return tag.trim().toLowerCase();
                    if (tag && tag.name) return tag.name.trim().toLowerCase();
                    return String(tag).trim().toLowerCase();
                }).filter(Boolean);
            }
            newTagIndex.set(contact.id, contactTags);
        });

        searchIndex.current = newSearchIndex;
        tagIndex.current = newTagIndex;
    }, [data]);

    return useMemo(() => {
        if (data.length === 0) return [];

        let result = [...data]; // Create a copy for safe mutation

        // Filter by search
        if (search.trim()) {
            const searchLower = search.toLowerCase();
            result = result.filter(contact => {
                const searchText = searchIndex.current.get(contact.id) || '';
                return searchText.includes(searchLower);
            });
        }

        // Filter by tags with mode consideration
        if (tags.length > 0) {
            result = result.filter(contact => {
                const contactTags = tagIndex.current.get(contact.id) || [];

                // Contact must have at least one tag
                if (contactTags.length === 0) return false;

                if (tagFilterMode === 'any') {
                    // "Any" mode - contact must have at least one of the selected tags (exact match, case-insensitive)
                    return tags.some(filterTag =>
                        contactTags.some(contactTag => contactTag === filterTag.toLowerCase())
                    );
                } else {
                    // "All" mode - contact must have all selected tags (exact match, case-insensitive)
                    return tags.every(filterTag =>
                        contactTags.some(contactTag => contactTag === filterTag.toLowerCase())
                    );
                }
            });
        }

        // Sorting
        if (sortField) {
            result.sort((a, b) => {
                let aValue = '';
                let bValue = '';

                switch (sortField) {
                    case 'name':
                        aValue = (a.name || '').toLowerCase();
                        bValue = (b.name || '').toLowerCase();
                        break;
                    case 'phone':
                        aValue = a.phone || '';
                        bValue = b.phone || '';
                        break;
                    case 'tags':
                        aValue = (tagIndex.current.get(a.id) || []).join(',');
                        bValue = (tagIndex.current.get(b.id) || []).join(',');
                        break;
                    default:
                        return 0;
                }

                if (sortDirection === 'desc') {
                    return bValue.localeCompare(aValue);
                } else {
                    return aValue.localeCompare(bValue);
                }
            });
        }

        return result;
    }, [data, search, tags, tagFilterMode, sortField, sortDirection]);
};

// Selected contact list item component
const SelectedContactItem = React.memo(({ contact, onRemove }) => (
    <ListItem dense divider>
        <Avatar sx={{ mr: 1, width: 32, height: 32 }}>
            <PersonIcon fontSize="small" />
        </Avatar>
        <ListItemText
            primary={
                <Typography variant="body2" noWrap>
                    {contact.name || 'No name'}
                </Typography>
            }
            secondary={
                <Typography variant="caption" color="text.secondary" noWrap>
                    {contact.phone}
                </Typography>
            }
        />
        <ListItemSecondaryAction>
            <IconButton
                edge="end"
                size="small"
                color="error"
                onClick={() => onRemove(contact.id)}
            >
                <DeleteIcon fontSize="small" />
            </IconButton>
        </ListItemSecondaryAction>
    </ListItem>
));

const ContactSelectionDialog = ({
                                    open,
                                    onClose,
                                    campaignContacts,
                                    onSave,
                                }) => {
    // Main states
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [search, setSearch] = useState('');
    const [tags, setTags] = useState([]);
    const [tagFilterMode, setTagFilterMode] = useState('any'); // 'any' or 'all'
    const [allContacts, setAllContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [availableTags, setAvailableTags] = useState([]);

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    // Sorting
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');

    // Optimized search with debounce
    const debouncedSearch = useDebounce(search, DEBOUNCE_DELAY);
    const debouncedTags = useDebounce(tags, DEBOUNCE_DELAY);

    // High-performance filtering and sorting
    const filteredContacts = useOptimizedFilter(
        allContacts,
        debouncedSearch,
        debouncedTags,
        tagFilterMode,
        sortField,
        sortDirection
    );

    // Paginated contacts
    const paginatedContacts = useMemo(() => {
        const startIndex = page * rowsPerPage;
        return filteredContacts.slice(startIndex, startIndex + rowsPerPage);
    }, [filteredContacts, page, rowsPerPage]);

    // Fast Sets for checking selected contacts
    const selectedContactsSet = useMemo(() =>
            new Set(selectedContacts.map(c => c.id)),
        [selectedContacts]
    );

    const filteredContactsSet = useMemo(() =>
            new Set(filteredContacts.map(c => c.id)),
        [filteredContacts]
    );

    // Restore selected contacts when opening
    useEffect(() => {
        if (open) {
            setSelectedContacts([...(campaignContacts || [])]);
        }
    }, [open, campaignContacts]);

    // Reset pagination when filters OR sorting change
    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, debouncedTags, tagFilterMode, sortField, sortDirection]);

    // Load data in chunks to avoid UI blocking
    const loadContactsInChunks = useCallback(async () => {
        setLoading(true);
        setLoadingProgress(0);

        try {
            // Load tags immediately
            const tagsPromise = contactService.getContactTags()
                .then(tags => {
                    if (Array.isArray(tags)) {
                        setAvailableTags(tags);
                    }
                })
                .catch(err => {
                    console.error('Error fetching tags:', err);
                    setAvailableTags([]);
                });

            // Load contacts
            const response = await contactService.getContacts({ limit: 10000000 });
            let allContactsData = [];

            if (response.data && Array.isArray(response.data)) {
                allContactsData = response.data;
            } else if (response.contacts && Array.isArray(response.contacts)) {
                allContactsData = response.contacts;
            } else if (Array.isArray(response)) {
                allContactsData = response;
            }

            // Process contacts in chunks to avoid UI blocking
            const processedContacts = [];

            for (let i = 0; i < allContactsData.length; i += CHUNK_SIZE) {
                const chunk = allContactsData.slice(i, i + CHUNK_SIZE);

                // Process chunk
                const processedChunk = chunk.map(contact => ({
                    ...contact,
                    // Pre-process tags for fast access
                    _processedTags: (() => {
                        if (!contact.tags) return [];
                        if (typeof contact.tags === 'string') {
                            return contact.tags.split(',').map(tag => tag.trim()).filter(Boolean);
                        }
                        if (Array.isArray(contact.tags)) {
                            return contact.tags.map(tag => {
                                if (typeof tag === 'string') return tag.trim();
                                if (tag && tag.name) return tag.name.trim();
                                return String(tag).trim();
                            }).filter(Boolean);
                        }
                        return [];
                    })()
                }));

                processedContacts.push(...processedChunk);

                // Update progress
                const progress = Math.round(((i + chunk.length) / allContactsData.length) * 100);
                setLoadingProgress(progress);

                // Allow UI to update
                if (i % (CHUNK_SIZE * 5) === 0) {
                    await new Promise(resolve => setTimeout(resolve, 0));
                }
            }

            setAllContacts(processedContacts);
            await tagsPromise;

        } catch (error) {
            console.error('Error loading contacts:', error);
            setAllContacts([]);
        } finally {
            setLoading(false);
            setLoadingProgress(0);
        }
    }, []);

    // Load data when dialog opens
    useEffect(() => {
        if (open && allContacts.length === 0) {
            loadContactsInChunks();
        }
    }, [open, allContacts.length, loadContactsInChunks]);

    // Search and tags handlers
    const handleSearchChange = useCallback((event) => {
        setSearch(event.target.value);
    }, []);

    const handleTagsChange = useCallback((event, newValue) => {
        const tagNames = newValue.map(tag =>
            typeof tag === 'string' ? tag : tag?.name || tag?.label || String(tag)
        );
        setTags(tagNames);
    }, []);

    // Tag filter mode toggle handler
    const handleTagFilterModeToggle = useCallback(() => {
        setTagFilterMode(prev => prev === 'any' ? 'all' : 'any');
    }, []);

    // Sorting handlers
    const handleSort = useCallback((field) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    }, [sortField]);

    // Pagination handlers
    const handleChangePage = useCallback((event, newPage) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    // Optimized contact actions
    const handleAddFiltered = useCallback(async () => {
        setActionLoading(true);
        try {
            // Simulate processing for UI
            await new Promise(resolve => setTimeout(resolve, 100));

            const contactsToAdd = filteredContacts.filter(
                contact => !selectedContactsSet.has(contact.id)
            );

            if (contactsToAdd.length > 0) {
                setSelectedContacts(prev => [...prev, ...contactsToAdd]);
            }
        } finally {
            setActionLoading(false);
        }
    }, [filteredContacts, selectedContactsSet]);

    const handleRemoveFiltered = useCallback(async () => {
        setActionLoading(true);
        try {
            // Simulate processing for UI
            await new Promise(resolve => setTimeout(resolve, 100));

            const remainingContacts = selectedContacts.filter(
                contact => !filteredContactsSet.has(contact.id)
            );
            setSelectedContacts(remainingContacts);
        } finally {
            setActionLoading(false);
        }
    }, [selectedContacts, filteredContactsSet]);

    const handleRemoveContact = useCallback((contactId) => {
        setSelectedContacts(prev => prev.filter(contact => contact.id !== contactId));
    }, []);

    const handleSave = useCallback(async () => {
        setActionLoading(true);
        try {
            // Simulate saving for UI
            await new Promise(resolve => setTimeout(resolve, 500));
            onSave(selectedContacts);
            onClose();
        } finally {
            setActionLoading(false);
        }
    }, [selectedContacts, onSave, onClose]);

    // Utility for formatting tags
    const formatTags = useCallback((contactTags) => {
        if (!contactTags) return [];

        if (Array.isArray(contactTags)) {
            return contactTags;
        }

        if (typeof contactTags === 'string') {
            return contactTags.split(',').map(tag => tag.trim()).filter(Boolean);
        }

        return [];
    }, []);

    // Contact count for buttons (optimized)
    const contactStats = useMemo(() => {
        const toAddCount = filteredContacts.filter(contact =>
            !selectedContactsSet.has(contact.id)
        ).length;

        const toRemoveCount = filteredContacts.filter(contact =>
            selectedContactsSet.has(contact.id)
        ).length;

        return { toAddCount, toRemoveCount };
    }, [filteredContacts, selectedContactsSet]);

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Manage Campaign Contacts</Typography>
                        {loading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption">
                                    Loading: {loadingProgress}%
                                </Typography>
                                <CircularProgress size={20} />
                            </Box>
                        )}
                    </Box>
                    {loading && (
                        <LinearProgress
                            variant="determinate"
                            value={loadingProgress}
                            sx={{ mt: 1 }}
                        />
                    )}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={3}>
                        {/* Left side - search and table */}
                        <Grid item xs={7}>
                            <Paper sx={{ p: 2, mb: 2 }}>
                                {/* Search */}
                                <Box sx={{ mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        placeholder="Search by name or phone..."
                                        value={search}
                                        onChange={handleSearchChange}
                                        disabled={loading}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>

                                {/* Tag filter with embedded mode toggle */}
                                <Box sx={{ mb: 1 }}>
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={availableTags}
                                        getOptionLabel={(option) =>
                                            typeof option === 'string' ? option : option?.name || String(option)
                                        }
                                        value={tags}
                                        onChange={handleTagsChange}
                                        disabled={loading}
                                        filterSelectedOptions
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    variant="outlined"
                                                    color="primary"
                                                    label={typeof option === 'string' ? option : option?.name || String(option)}
                                                    {...getTagProps({ index })}
                                                    key={typeof option === 'string' ? option : option?.name || index}
                                                    size="small"
                                                />
                                            ))
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Filter by tags..."
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <InputAdornment position="start">
                                                                <LabelIcon fontSize="small" />
                                                            </InputAdornment>
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    ),
                                                    endAdornment: (
                                                        <>
                                                            {/* Tag filter mode toggle button */}
                                                            <InputAdornment position="end">
                                                                <Tooltip
                                                                    title={tagFilterMode === 'any'
                                                                        ? "Currently: Show contacts with ANY of the selected tags. Click to switch to ALL tags mode."
                                                                        : "Currently: Show contacts with ALL selected tags. Click to switch to ANY tags mode."
                                                                    }
                                                                >
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={handleTagFilterModeToggle}
                                                                        disabled={loading}
                                                                        color={tagFilterMode === 'all' ? 'primary' : 'default'}
                                                                        sx={{
                                                                            mr: 1,
                                                                            '&:hover': {
                                                                                backgroundColor: 'action.hover'
                                                                            }
                                                                        }}
                                                                    >
                                                                        {tagFilterMode === 'any' ? (
                                                                            <FilterListOffIcon fontSize="small" />
                                                                        ) : (
                                                                            <FilterListIcon fontSize="small" />
                                                                        )}
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </InputAdornment>
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Box>

                                {/* Filter mode description */}
                                {tags.length > 0 && (
                                    <Box sx={{ mb: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {tagFilterMode === 'any'
                                                ? `Showing contacts with ANY of the ${tags.length} selected tag${tags.length > 1 ? 's' : ''}`
                                                : `Showing contacts with ALL ${tags.length} selected tags`
                                            }
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>

                            {/* Contacts table with sorting */}
                            <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortField === 'name'}
                                                    direction={sortField === 'name' ? sortDirection : 'asc'}
                                                    onClick={() => handleSort('name')}
                                                >
                                                    Name
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortField === 'phone'}
                                                    direction={sortField === 'phone' ? sortDirection : 'asc'}
                                                    onClick={() => handleSort('phone')}
                                                >
                                                    Phone
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortField === 'tags'}
                                                    direction={sortField === 'tags' ? sortDirection : 'asc'}
                                                    onClick={() => handleSort('tags')}
                                                >
                                                    Tags
                                                </TableSortLabel>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loading ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, p: 2 }}>
                                                        <CircularProgress size={24} />
                                                        <Typography>Loading contacts...</Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : paginatedContacts.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    {allContacts.length === 0 ? 'No contacts available' : 'No contacts found with current filters'}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            paginatedContacts.map((contact) => (
                                                <TableRow key={contact.id}>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                                                                <PersonIcon fontSize="small" />
                                                            </Avatar>
                                                            {contact.name || 'No name'}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {contact.phone}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {formatTags(contact.tags).slice(0, 3).map((tag, index) => (
                                                                <Chip
                                                                    key={index}
                                                                    label={tag}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            ))}
                                                            {formatTags(contact.tags).length > 3 && (
                                                                <Chip
                                                                    label={`+${formatTags(contact.tags).length - 3}`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="default"
                                                                />
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination */}
                            <TablePagination
                                component="div"
                                count={filteredContacts.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                                labelRowsPerPage="Rows per page:"
                                labelDisplayedRows={({ from, to, count }) =>
                                    `${from}-${to} of ${count !== -1 ? count.toLocaleString() : `more than ${to}`}`
                                }
                            />

                            {/* Statistics */}
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Total contacts: {allContacts.length.toLocaleString()} |
                                    Found: {filteredContacts.length.toLocaleString()} |
                                    Selected: {selectedContacts.length.toLocaleString()}
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Vertical divider */}
                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                        {/* Right side - selected contacts */}
                        <Grid item xs={4}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Selected Contacts ({selectedContacts.length.toLocaleString()})
                            </Typography>
                            <Paper sx={{ maxHeight: 550, overflow: 'auto' }}>
                                <List dense>
                                    {selectedContacts.map((contact) => (
                                        <SelectedContactItem
                                            key={contact.id}
                                            contact={contact}
                                            onRemove={handleRemoveContact}
                                        />
                                    ))}
                                    {selectedContacts.length === 0 && (
                                        <ListItem>
                                            <ListItemText primary="No contacts selected" />
                                        </ListItem>
                                    )}
                                </List>
                            </Paper>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
                    {/* Left buttons */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={actionLoading ? <CircularProgress size={16} /> : <AddIcon />}
                            onClick={handleAddFiltered}
                            disabled={loading || actionLoading || contactStats.toAddCount === 0}
                        >
                            Add ({contactStats.toAddCount.toLocaleString()})
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={actionLoading ? <CircularProgress size={16} /> : <RemoveIcon />}
                            onClick={handleRemoveFiltered}
                            disabled={loading || actionLoading || contactStats.toRemoveCount === 0}
                        >
                            Remove ({contactStats.toRemoveCount.toLocaleString()})
                        </Button>
                    </Box>

                    {/* Right buttons */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={onClose} disabled={loading || actionLoading}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            onClick={handleSave}
                            disabled={loading || actionLoading}
                        >
                            Save ({selectedContacts.length.toLocaleString()})
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Backdrop for long operations */}
            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.modal + 1,
                    flexDirection: 'column',
                    gap: 2
                }}
                open={actionLoading}
            >
                <CircularProgress color="inherit" size={60} />
                <Typography variant="h6">
                    Processing contacts...
                </Typography>
            </Backdrop>
        </>
    );
};

export default ContactSelectionDialog;